<script setup lang="ts">
/**
 * Investor bank-accounts directory container.
 *
 * Composes:
 *   - list query (server-side pagination + filters)
 *   - filter drawer with first-name / last-name / verification status
 *     / currency
 *   - details drawer (renders from selected row — no detail endpoint
 *     yet on admin-service for investor bank accounts)
 *
 * Row-click and a row-actions kebab both open the drawer. A primary
 * "View transactions" CTA in the drawer footer (and a sibling row
 * action) navigates to the fiat-transactions list with this bank
 * account pre-filtered via `?investorBankAccountId=`.
 */
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Message from '@uiKit/elements/Message.vue'
import AppDrawer from '@uiKit/elements/Drawer.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import Avatar from '@uiKit/components/Avatar.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'

import { useFilterUrlSync } from '@shared/composables/useFilterUrlSync'
import { formatToInternational, fullName } from '@shared/utils'
import { routes } from '@app/router/routes'

import BankAccountsDirectoryFilterDrawer, {
  type BankAccountsDirectoryFilterValues,
} from '../components/BankAccountsDirectoryFilterDrawer.vue'
import BankAccountsDirectoryList from '../components/BankAccountsDirectoryList.vue'
import BankAccountDirectoryDetail from '../components/BankAccountDirectoryDetail.vue'

import { useBankAccountsDirectoryListQuery } from '../api/queries/useBankAccountsDirectoryListQuery'
import { useBankAccountDirectoryDrawer } from '../composables/useBankAccountDirectoryDrawer'

import {
  getBankAccountStatusColor,
  getBankAccountStatusLabel,
} from '../utils/bankAccountsDirectoryFormatters'

import {
  BANK_ACCOUNT_STATUS,
  type BankAccountRow,
  type BankAccountStatus,
  type GetBankAccountsDirectoryParams,
} from '../types/bankAccountsDirectoryTypes'

// ─── Filters + pagination (server-side) ─────────────────────────────────────

const currentPage = ref(1)
const pageSize = ref(50)

const filterValues = ref<BankAccountsDirectoryFilterValues>({
  firstName: '',
  lastName: '',
  investorEmail: '',
  bankName: '',
  accountStatus: null,
  currencyCode: '',
})

const filterDrawerVisible = ref(false)

useFilterUrlSync(filterValues, {
  toQuery: (state) => ({
    firstName: state.firstName || undefined,
    lastName: state.lastName || undefined,
    investorEmail: state.investorEmail || undefined,
    bankName: state.bankName || undefined,
    accountStatus: state.accountStatus ?? undefined,
    currencyCode: state.currencyCode || undefined,
  }),
  fromQuery: (query) => {
    const seed: Partial<BankAccountsDirectoryFilterValues> = {}
    if (typeof query.firstName === 'string') seed.firstName = query.firstName
    if (typeof query.lastName === 'string') seed.lastName = query.lastName
    if (typeof query.investorEmail === 'string') seed.investorEmail = query.investorEmail
    if (typeof query.bankName === 'string') seed.bankName = query.bankName
    if (
      typeof query.accountStatus === 'string' &&
      Object.values(BANK_ACCOUNT_STATUS).includes(query.accountStatus as BANK_ACCOUNT_STATUS)
    ) {
      seed.accountStatus = query.accountStatus as BankAccountStatus
    }
    if (typeof query.currencyCode === 'string') seed.currencyCode = query.currencyCode
    return seed
  },
  onFiltersChange: () => {
    currentPage.value = 1
  },
})

const listParams = computed<GetBankAccountsDirectoryParams>(() => {
  const params: GetBankAccountsDirectoryParams = {
    page: currentPage.value,
    limit: pageSize.value,
  }
  if (filterValues.value.firstName) params.firstName = filterValues.value.firstName
  if (filterValues.value.lastName) params.lastName = filterValues.value.lastName
  if (filterValues.value.investorEmail) params.investorEmail = filterValues.value.investorEmail
  if (filterValues.value.bankName) params.bankName = filterValues.value.bankName
  if (filterValues.value.accountStatus) params.accountStatus = filterValues.value.accountStatus
  if (filterValues.value.currencyCode) params.currencyCode = filterValues.value.currencyCode
  return params
})

const {
  data: listData,
  isLoading: listLoading,
  isFetching: listFetching,
  error: listError,
  refetch: refetchList,
} = useBankAccountsDirectoryListQuery(listParams)

const totalAccounts = computed(() => listData.value?.rowsCount ?? 0)
const headerCountLabel = computed(() => {
  if (listLoading.value) return '…'
  return `${formatToInternational(totalAccounts.value)} accounts`
})

function onFiltersApply(next: BankAccountsDirectoryFilterValues) {
  filterValues.value = { ...next }
  currentPage.value = 1
}

function onFiltersReset() {
  filterValues.value = {
    firstName: '',
    lastName: '',
    investorEmail: '',
    bankName: '',
    accountStatus: null,
    currencyCode: '',
  }
  currentPage.value = 1
}

const activeFilterCount = computed(() => {
  const f = filterValues.value
  let count = 0
  if (f.firstName) count += 1
  if (f.lastName) count += 1
  if (f.investorEmail) count += 1
  if (f.bankName) count += 1
  if (f.accountStatus) count += 1
  if (f.currencyCode) count += 1
  return count
})

const hasActiveFilters = computed(() => activeFilterCount.value > 0)

const emptyTitle = computed(() =>
  hasActiveFilters.value ? 'No bank accounts match your filters' : 'No bank accounts',
)
const emptySubtitle = computed(() =>
  hasActiveFilters.value
    ? 'Clear filters to see all bank accounts.'
    : 'Investor bank accounts will appear here as they are added.',
)

function openFilterDrawer() {
  filterDrawerVisible.value = true
}

function describeError(err: unknown): string {
  if (!err) return ''
  if (err instanceof Error) return err.message
  const candidate = err as { message?: unknown; code?: unknown }
  if (typeof candidate.message === 'string') return candidate.message
  if (typeof candidate.code === 'string') return candidate.code
  return 'Something went wrong. Please try again.'
}

// ─── Drawer ─────────────────────────────────────────────────────────────────

const { drawerVisible, selectedRow, openDrawer, closeDrawer } = useBankAccountDirectoryDrawer()

const drawerInvestorName = computed(() => {
  const row = selectedRow.value
  if (!row) return ''
  return fullName(row.investor.firstName, row.investor.lastName) || row.bankName || ''
})

const drawerSubtitle = computed(() => selectedRow.value?.accountHolderName ?? '')

// ─── View transactions deep link ────────────────────────────────────────────

const router = useRouter()

function goToTransactions(row: BankAccountRow) {
  router.push({
    path: routes.TREASURY_FIAT_TRANSACTIONS_LIST,
    query: { investorBankAccountId: row.id },
  })
}

function handleDrawerViewTransactions(row: BankAccountRow) {
  closeDrawer()
  goToTransactions(row)
}
</script>

<template>
  <Box flex direction="col" gap="4">
    <Message v-if="listError" color="danger" variant="outlined" size="small">
      <Box flex justify="between" align="center" gap="3">
        <Text type="body-sm">Could not load bank accounts. {{ describeError(listError) }}</Text>
        <Button label="Try again" variant="text" color="danger" @click="refetchList()" />
      </Box>
    </Message>

    <BankAccountsDirectoryList
      title="Investor bank accounts"
      :subtitle="headerCountLabel"
      :rows="listData?.rows ?? []"
      :loading="listLoading || listFetching"
      :total="totalAccounts"
      :page="currentPage"
      :page-size="pageSize"
      :filter-count="activeFilterCount"
      :empty-title="emptyTitle"
      :empty-subtitle="emptySubtitle"
      @update:page="currentPage = $event"
      @update:page-size="pageSize = $event"
      @filter-click="openFilterDrawer"
      @row-click="openDrawer"
      @view-details="openDrawer"
      @view-transactions="goToTransactions"
    />
  </Box>

  <BankAccountsDirectoryFilterDrawer
    :visible="filterDrawerVisible"
    :values="filterValues"
    @update:visible="filterDrawerVisible = $event"
    @apply="onFiltersApply"
    @reset="onFiltersReset"
  />

  <AppDrawer
    :model-value="drawerVisible"
    position="right"
    width="540px"
    :show-close-icon="false"
    @update:model-value="(v) => !v && closeDrawer()"
  >
    <template #header>
      <DrawerHeader
        :title="drawerInvestorName || 'Bank account'"
        :subtitle="drawerSubtitle"
        @close="closeDrawer"
      >
        <template #avatar>
          <Avatar
            :id="selectedRow?.investor.id ?? ''"
            :name="drawerInvestorName || (selectedRow?.bankName ?? '')"
          />
        </template>
        <template #extras>
          <StatusBadge
            v-if="selectedRow"
            :color="getBankAccountStatusColor(selectedRow.accountStatus)"
            :label="getBankAccountStatusLabel(selectedRow.accountStatus)"
          />
        </template>
      </DrawerHeader>
    </template>

    <BankAccountDirectoryDetail
      v-if="selectedRow"
      :row="selectedRow"
      @view-transactions="handleDrawerViewTransactions"
    />
  </AppDrawer>
</template>
